import { createChatMessage, toLocalTimestamp } from '../../utils/chat-utils.js';
import { config } from '../../config/environment.js';
import type { MessageHistory } from '../../services/types.js';
import type { ChatMessage } from './types.js';
import { buildAssistantMessagesFromReconnect } from './message-adapter.js';

const HISTORY_SCROLL_DELAY_MS = 150;

function hasErrorTypeInPayload(value: unknown): boolean {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === 'error') return true;

        // Reconnect payloads may arrive stringified; decode and inspect recursively.
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                const parsed = JSON.parse(trimmed) as unknown;
                return hasErrorTypeInPayload(parsed);
            } catch {
                return false;
            }
        }

        return false;
    }

    if (!value || typeof value !== 'object') return false;

    if (Array.isArray(value)) {
        return value.some((item) => hasErrorTypeInPayload(item));
    }

    const record = value as Record<string, unknown>;
    if (record.type === 'error') return true;

    return Object.values(record).some((entry) => hasErrorTypeInPayload(entry));
}

export function buildMessagesFromReconnectHistory(
    history: MessageHistory[] | undefined,
    chatCreatedAt?: string,
    source?: string
): ChatMessage[] {
    const bffHistory = history ?? [];
    const initialText = source?.toLowerCase() === 'col'
        ? config.MESSAGES.COL_INITIAL_MESSAGE
        : config.MESSAGES.INITIAL_MESSAGE;
    const initialMessage = createChatMessage(initialText, 'assistant', 'incoming');
    if (chatCreatedAt) initialMessage.sentTime = toLocalTimestamp(chatCreatedAt);

    // actionsLocked is now derived at render time in mariana-chat.ts (messagesWithLockState),
    // so no post-processing needed here.
    const isCol = source?.toLowerCase() === 'col';

    return [
        initialMessage,
        ...bffHistory.flatMap((item, index) => {
            const msgs: ChatMessage[] = [];
            const responseText = hasErrorTypeInPayload(item.response_extra)
                ? config.MESSAGES.GENERIC_ERROR_MESSAGE
                : item.response;

            // For COL, the first history item is the BFF trigger for the initial menu —
            // skip its user message since it's an internal trigger, not a real user message.
            const skipUserMessage = isCol && index === 0;

            if (item.message && !skipUserMessage) {
                const userMsg = createChatMessage(item.message, 'user', 'outgoing');
                if (item.created_at) userMsg.sentTime = toLocalTimestamp(item.created_at);
                msgs.push(userMsg);
            }
            const assistantMsgs = buildAssistantMessagesFromReconnect(responseText, item.response_extra);
            if (item.reply_received_stamp) {
                for (const msg of assistantMsgs) msg.sentTime = toLocalTimestamp(item.reply_received_stamp);
            }
            msgs.push(...assistantMsgs);
            return msgs;
        }),
    ];
}

export function animateHistoryScrollToBottom(shadowRoot: ShadowRoot | null): void {
    const messageList = shadowRoot?.querySelector<HTMLElement>('.message-list');
    if (!messageList || messageList.scrollHeight <= messageList.clientHeight) return;

    messageList.scrollTop = 0;
    globalThis.setTimeout(() => {
        messageList.scrollTo({ top: messageList.scrollHeight, behavior: 'smooth' });
    }, HISTORY_SCROLL_DELAY_MS);
}
